import Button from '@atlaskit/button';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { FormattedMessage } from 'react-intl-next';

import { requestStorageAccess, StorageAccessState } from '@townsquare/auth-utils';
import { location } from '@townsquare/facade';
import { TownsquareIntlProvider } from '@townsquare/i18n';

import { FullScreenSpaLoader } from '../ui/FullScreenSpaLoader';

import { loginViaPopup } from './popupLogin';
import { prefetchedAuthCheckPromise } from './prefetch';

type RequestStorageAccessViewProps = {
  spotImgSrc: string;
  title: JSX.Element;
  subtitle?: JSX.Element;
  buttonText: JSX.Element;
  buttonOnClick: () => void | Promise<void>;
  buttonDisabled?: boolean;
};

const containerStyles = xcss({
  width: '100vw',
  height: '100vh',
  textAlign: 'center',
  background: 'color.background.neutral',
});

const innerContainerStyles = xcss({
  boxSizing: 'border-box',
  flex: 1,
  // @ts-expect-error - not a support DS point, likely need to re-think this application.
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
  ['@media (max-height: 300px)']: {
    flexDirection: 'row',
  },
});

const imageContainerStyles = xcss({
  height: '120px',
  width: '120px',
  boxSizing: 'border-box',
});

const textAndButtonFlexColumnStyles = xcss({
  padding: 'space.150',
});

const RequestStorageAccessBaseComponent = ({
  spotImgSrc,
  title,
  subtitle,
  buttonText,
  buttonOnClick,
}: RequestStorageAccessViewProps) => {
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const wrappedOnClick = async () => {
    // Prevent triggering buttonOnClick again while it's still running
    setButtonDisabled(true);
    try {
      await buttonOnClick();
    } finally {
      setButtonDisabled(false);
    }
  };

  return (
    <Flex alignItems="center" xcss={containerStyles}>
      <Flex direction="column" justifyContent="center" alignItems="center" gap="space.200" xcss={innerContainerStyles}>
        <Box padding="space.200" xcss={imageContainerStyles}>
          {/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
          <img src={spotImgSrc} key={spotImgSrc} alt="" style={{ maxHeight: '100%', maxWidth: '100%' }} />
        </Box>
        <Flex direction="column" alignItems="center" gap="space.200" xcss={textAndButtonFlexColumnStyles}>
          <Flex direction="column" alignItems="center" gap="space.150" testId="request-access-content">
            <Text weight="bold">{title}</Text>
            {subtitle}
          </Flex>
          <Button
            appearance="primary"
            onClick={wrappedOnClick}
            isDisabled={buttonDisabled}
            testId="request-access-action-button"
          >
            {buttonText}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export function StorageAuthCheck(props: PropsWithChildren<Record<never, never>>) {
  return (
    <TownsquareIntlProvider>
      <RequestStorageAccess>{props.children}</RequestStorageAccess>
    </TownsquareIntlProvider>
  );
}

export function RequestStorageAccess(props: PropsWithChildren<Record<never, never>>) {
  const [storageAccessState, setStorageAccessState] = useState(StorageAccessState.Unknown);
  const [noAccess, setNoAccess] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  const performChecks = useCallback(async () => {
    const storageState = await requestStorageAccess();
    const { isAuthed } = await prefetchedAuthCheckPromise;

    unstable_batchedUpdates(() => {
      setStorageAccessState(storageState);
      setAuthenticated(isAuthed);
    });
  }, []);

  useEffect(() => {
    performChecks();
  }, [performChecks]);

  const onClick = useCallback(async () => {
    setNoAccess(false);
    try {
      await document.requestStorageAccess();
      performChecks();
    } catch (e) {
      setNoAccess(true);
    }
  }, [performChecks]);

  const openThirdPartyCookiesHelpArticle = useCallback(() => {
    window.open(
      'https://intercom.help/atlas-by-atlassian/en/articles/8593719-troubleshooting-the-jira-software-integration',
      '_blank',
    );
  }, []);

  const onSignInClick = useCallback(() => {
    loginViaPopup({
      onAuthenticated: () => {
        location().reload();
      },
      onUnauthenticated: () => {
        location().reload();
      },
    });
  }, []);

  if (storageAccessState === StorageAccessState.Unknown) {
    return <FullScreenSpaLoader />;
  }

  if (noAccess) {
    return (
      <RequestStorageAccessBaseComponent
        spotImgSrc="/Images/states/generic-error.svg"
        title={
          <FormattedMessage
            id="townsquare.web.request-storage-access.no-access"
            defaultMessage="Browser access could not be requested"
            description="Text for when a user is unable to request storage access."
          />
        }
        subtitle={
          <FormattedMessage
            id="townsquare.web.request-storage-access.enable-third-party-cookies"
            defaultMessage="Enable third-party cookies in your browser and try again."
            description="Prompts users to enable third-party cookies and retry requesting storage access."
          />
        }
        buttonText={
          <FormattedMessage
            id="townsquare.web.request-storage-access.find-out-how-button"
            defaultMessage="Find out how"
            description="Button text for linking out to the documentation on enabling third-party cookies."
          />
        }
        buttonOnClick={openThirdPartyCookiesHelpArticle}
      />
    );
  }

  if (storageAccessState === StorageAccessState.UserMustGrant) {
    return (
      <RequestStorageAccessBaseComponent
        spotImgSrc="/Images/RequestStorageAccess/Gears.svg"
        title={
          <FormattedMessage
            id="townsquare.home.request-storage-access.embed-text"
            defaultMessage="Atlas needs additional browser permissions"
            description="Text shown in embed for when a user needs to request storage access."
          />
        }
        buttonText={
          <FormattedMessage
            id="townsquare.home.request-storage-access.authorize"
            defaultMessage="Allow access"
            description="Button text for when a user needs to request storage access."
          />
        }
        buttonOnClick={onClick}
      />
    );
  }

  if (!authenticated) {
    return (
      <RequestStorageAccessBaseComponent
        spotImgSrc="/Images/FirewallKey@2x.png"
        title={
          <FormattedMessage
            id="townsquare.web.request-storage-access.need-auth"
            defaultMessage="Looks like you haven't signed in yet"
            description="Text for when a user needs to sign in after requesting storage access."
          />
        }
        buttonText={
          <FormattedMessage
            id="townsquare.web.request-storage-access.sign-in"
            defaultMessage="Sign in"
            description="Button text for when a user needs to sign in after requesting storage access."
          />
        }
        buttonOnClick={onSignInClick}
      />
    );
  }

  return <>{props.children}</>;
}
